/**
 * The theme switch, and the one rule that keeps it honest.
 *
 * `useMantineColorScheme()` returns a `colorScheme` that may be the string 'auto'. A toggle that
 * tests `colorScheme === 'dark'` is therefore wrong under the default setting — Mantine's own
 * documentation marks that check "Incorrect", because under 'auto' it is neither. Read
 * `useComputedColorScheme()` instead, which resolves 'auto' against the reader's system setting
 * (docs/ui/02-theme.md).
 *
 * This module also carries the ATOMIC THEME SWAP, which is one of the four non-optional global
 * rules. Its CSS half lives in ./global.css; the half that has to run is here.
 */
import { useCallback } from 'react';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';

/** The attribute ./global.css keys its transition kill-switch off. */
const SWAPPING_ATTRIBUTE = 'data-mx-theme-swapping';

/**
 * Runs `swap` with every CSS transition disabled for exactly one reflow, so the whole page
 * changes theme at once instead of cross-fading element by element. The brand repo's own comment
 * records that the cross-fade version produced real bug reports in Safari.
 *
 * The forced read of `offsetHeight` between setting and clearing the attribute is the point of
 * the function: it flushes style and layout, which is what makes "one reflow" true rather than
 * hopeful. Without it the browser may batch all three steps and the transitions run anyway.
 */
export function withAtomicThemeSwap(swap: () => void): void {
  const root = document.documentElement;
  root.setAttribute(SWAPPING_ATTRIBUTE, '');
  swap();
  // Deliberate forced reflow. Read the value and discard it.
  void root.offsetHeight;
  root.removeAttribute(SWAPPING_ATTRIBUTE);
}

export interface ThemeToggle {
  /** 'light' or 'dark' — never 'auto'. Safe to compare against. */
  readonly computed: 'light' | 'dark';
  /** Flip between light and dark, atomically. */
  readonly toggle: () => void;
  /** Hand the choice back to the reader's system setting. */
  readonly clear: () => void;
}

/**
 * The hook every theme toggle in either app uses. It exists so no screen has to remember either
 * of the two rules above.
 */
export function useThemeToggle(): ThemeToggle {
  const { setColorScheme, clearColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('dark', { getInitialValueInEffect: true });

  const toggle = useCallback(() => {
    withAtomicThemeSwap(() => {
      setColorScheme(computed === 'dark' ? 'light' : 'dark');
    });
  }, [computed, setColorScheme]);

  const clear = useCallback(() => {
    withAtomicThemeSwap(() => {
      clearColorScheme();
    });
  }, [clearColorScheme]);

  return { computed, toggle, clear };
}
